import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { setToken } from '@/lib/tokenStorage';
import { logger } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_ITEMS = [
  { text: 'Oil futures surge on Middle East supply fears', source: '@energy_desk', score: 4, instruments: [{ symbol: 'OIL', score: 5 }, { symbol: 'NATGAS', score: 3 }] },
  { text: 'Fed signals potential rate pause amid inflation data', source: '@NickTimiraos', score: -1, instruments: [{ symbol: 'UST', score: -2 }, { symbol: 'GOLD', score: 1 }] },
  { text: 'European gas prices spike on pipeline disruption', source: '@JavierBlas', score: -3, instruments: [{ symbol: 'LNG', score: -3 }, { symbol: 'EUR', score: -1 }] },
  { text: 'S&P 500 hits new highs on tech earnings beat', source: '@zerohedge', score: 3, instruments: [{ symbol: 'SPX', score: 2 }, { symbol: 'NDX', score: 4 }] },
  { text: 'OPEC+ considers deeper production cuts', source: '@OilHeadlineNews', score: 4, instruments: [{ symbol: 'OIL', score: 5 }, { symbol: 'BTC', score: 1 }] },
  { text: 'China PMI data misses expectations', source: '@sentdefender', score: -2, instruments: [{ symbol: 'SPX', score: -1 }, { symbol: 'CORN', score: -2 }] },
  { text: 'Gold rallies as dollar weakens on dovish Fed', source: '@FirstSquawk', score: 2, instruments: [{ symbol: 'GOLD', score: 4 }, { symbol: 'VIX', score: 3 }] },
  { text: 'Defense stocks surge after NATO spending commitment', source: '@WatcherGuru', score: 3, instruments: [{ symbol: '$LMT', score: 2 }, { symbol: 'VIX', score: -1 }] },
];

const CASCADE_TAGS = [
  'ENERGY', 'DEFENSE', 'GEOPOLITICAL RISK', 'SUPPLY CHAIN',
  'COMMODITIES', 'MIDDLE EAST REGIONAL', 'FISCAL POLICY',
  'MONETARY POLICY', 'TRADE', 'VOLATILITY',
];

function getScoreColor(score) {
  if (score > 0) return '#22c55e';
  if (score < 0) return '#ef4444';
  return '#8b949e';
}

function CascadeBackground({ items, warp }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const numCols = isMobile ? 5 : 12;
  const maxPerCol = isMobile ? 2 : 3;

  const cascadeItems = useMemo(() => {
    const source = items.length > 0 ? items : FALLBACK_ITEMS;
    const result = [];

    for (let col = 0; col < numCols; col++) {
      const count = 1 + Math.floor(Math.random() * maxPerCol);

      for (let i = 0; i < count; i++) {
        const rand = Math.random();
        const post = source[Math.floor(Math.random() * source.length)];
        let text, color, fontSize, duration;

        if (rand < 0.3 && post.text) {
          text = post.text.slice(0, 60) + (post.text.length > 60 ? '...' : '');
          color = getScoreColor(post.score);
          fontSize = 14;
          duration = 28 + Math.random() * 8;
        } else if (rand < 0.8 && post.instruments?.length > 0) {
          const inst = post.instruments[Math.floor(Math.random() * post.instruments.length)];
          text = `${inst.symbol} ${inst.score > 0 ? '+' : ''}${inst.score}`;
          color = getScoreColor(inst.score);
          fontSize = 16;
          duration = 18 + Math.random() * 8;
        } else {
          if (Math.random() < 0.5) {
            text = `${post.score > 0 ? '+' : ''}${post.score}`;
            color = getScoreColor(post.score);
          } else {
            text = CASCADE_TAGS[Math.floor(Math.random() * CASCADE_TAGS.length)];
            color = '#f59e0b';
          }
          fontSize = 12;
          duration = 12 + Math.random() * 8;
        }

        result.push({
          key: `${col}-${i}`,
          text,
          color,
          fontSize,
          duration,
          delay: -(Math.random() * duration),
          left: (col / numCols) * 100 + Math.random() * (100 / numCols * 0.3),
        });
      }
    }
    return result;
  }, [items, numCols, maxPerCol]);

  return (
    <div
      className="fixed inset-0 overflow-hidden pointer-events-none"
      style={{ opacity: 0.7, zIndex: 0 }}
    >
      {cascadeItems.map(item => (
        <div
          key={item.key}
          className="absolute whitespace-nowrap"
          style={{
            left: `${item.left}%`,
            color: item.color,
            fontSize: item.fontSize,
            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            animation: `cascadeFall ${warp ? item.duration / 5 : item.duration}s linear ${item.delay}s infinite`,
            willChange: 'transform',
            transform: 'translate3d(0,0,0)',
            contain: 'layout style paint',
          }}
        >
          {item.text}
        </div>
      ))}
    </div>
  );
}

export default function LoginScreen({ onAuthenticated }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cascadeData, setCascadeData] = useState([]);
  const [phase, setPhase] = useState('idle');
  const inputRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/v1/sentinel/cascade`)
      .then(res => setCascadeData(res.data.items || []))
      .catch((err) => { logger.debug("[Login] Cascade fetch:", err.message); });
    // Check lockout status on mount
    axios.get(`${BACKEND_URL}/api/v1/sentinel/auth/lockout-status`)
      .then(res => {
        if (res.data.locked) {
          setLockout({ locked: true, remaining: res.data.remaining });
        }
      })
      .catch((err) => { logger.debug("[Login] Lockout check:", err.message); });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 300);
    return () => clearTimeout(t);
  }, []);

  const [blocked, setBlocked] = useState(false);
  const [lockout, setLockout] = useState({ locked: false, remaining: 0 });

  // Countdown timer for lockout
  useEffect(() => {
    if (!lockout.locked || lockout.remaining <= 0) return;
    const interval = setInterval(() => {
      setLockout(prev => {
        const next = prev.remaining - 1;
        if (next <= 0) {
          return { locked: false, remaining: 0 };
        }
        return { ...prev, remaining: next };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [lockout.locked, lockout.remaining]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim() || loading) return;

    setLoading(true);
    setError('');

    try {
      const res = await axios.post(`${BACKEND_URL}/api/v1/sentinel/auth/login`, { password });
      setToken(res.data.token);

      setPhase('success');
      setTimeout(() => setPhase('flash'), 500);
      setTimeout(() => setPhase('entering'), 650);
      setTimeout(() => onAuthenticated(), 950);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.status === 'blocked') {
        setBlocked(true);
      } else if (err.response?.status === 429) {
        const detail = err.response?.data?.detail || {};
        setLockout({ locked: true, remaining: detail.remaining || 300 });
        setPassword('');
        setError('');
      } else {
        setError('Wrong password. Try again.');
        setPassword('');
        if (inputRef.current) {
          inputRef.current.classList.add('mf-shake');
          setTimeout(() => inputRef.current?.classList.remove('mf-shake'), 300);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Lockout Screen ───────────────────────────────────────────
  if (lockout.locked && lockout.remaining > 0) {
    const mins = Math.floor(lockout.remaining / 60);
    const secs = lockout.remaining % 60;
    return (
      <div
        data-testid="lockout-screen"
        className="fixed inset-0 flex items-center justify-center"
        style={{ backgroundColor: '#0d1117' }}
      >
        <div className="text-center px-6" style={{ maxWidth: 480 }}>
          {/* Pulsing red shield icon */}
          <div className="flex justify-center mb-6">
            <div
              className="flex items-center justify-center rounded-full"
              style={{
                width: 80, height: 80,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '2px solid rgba(239, 68, 68, 0.3)',
                animation: 'lockout-pulse 2s ease-in-out infinite',
              }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
          </div>

          <h1
            className="font-mono text-2xl font-bold tracking-wide mb-2"
            style={{ color: '#ef4444', letterSpacing: '0.15em' }}
          >
            ACCESS DENIED
          </h1>
          <p className="font-mono text-sm mb-8" style={{ color: '#8b949e' }}>
            Too many failed attempts. Terminal locked.
          </p>

          {/* Countdown */}
          <div
            className="inline-block px-6 py-3 rounded-lg mb-6"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.06)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}
          >
            <span className="font-mono text-3xl font-bold" style={{ color: '#ef4444' }}>
              {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
            </span>
          </div>

          <p className="font-mono text-xs" style={{ color: '#484f58' }}>
            Access will restore automatically.
          </p>
        </div>

        <style>{`
          @keyframes lockout-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.05); }
          }
        `}</style>
      </div>
    );
  }

  // ─── Permanent Block Screen ───────────────────────────────────
  if (blocked) {
    return (
      <div
        data-testid="blocked-screen"
        className="fixed inset-0 flex items-center justify-center"
        style={{ backgroundColor: '#0d1117' }}
      >
        <div className="text-center px-6" style={{ maxWidth: 500 }}>
          <div className="font-mono text-6xl mb-6" style={{ lineHeight: 1.2 }}>
            <span style={{ color: '#ef4444' }}>4</span>
            <span style={{ color: '#f59e0b' }}>0</span>
            <span style={{ color: '#ef4444' }}>3</span>
          </div>
          <h1 className="font-mono text-xl font-bold mb-3" style={{ color: '#e6edf3' }}>
            Sorry, you don't belong here.
          </h1>
          <p className="font-mono text-sm mb-6" style={{ color: '#8b949e' }}>
            Your IP has been blocked from accessing this terminal.
            Maybe try knocking next time?
          </p>
          <div className="font-mono text-xs" style={{ color: '#30363d' }}>
            If this is a mistake, contact the operator.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{
        backgroundColor: '#0d1117',
        transition: 'opacity 300ms ease',
        opacity: phase === 'entering' ? 0 : 1,
      }}
    >
      <CascadeBackground items={cascadeData} warp={phase === 'success' || phase === 'flash'} />

      {(phase === 'flash' || phase === 'entering') && (
        <div
          className="fixed inset-0 z-20 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at center, rgba(255,107,43,0.4) 0%, transparent 70%)',
            animation: 'mfFlashPulse 300ms ease-out forwards',
          }}
        />
      )}

      <div
        className="relative z-10 flex flex-col items-center px-6"
        style={{
          transition: 'opacity 200ms ease, transform 200ms ease',
          opacity: phase !== 'idle' ? 0 : 1,
          transform: phase !== 'idle' ? 'scale(0.95)' : 'scale(1)',
        }}
      >
        <img
          src="/logo-dark.png"
          alt="MacroFlare"
          className="mb-8"
          style={{ height: 220, objectFit: 'contain' }}
          data-testid="login-logo"
        />

        <form onSubmit={handleSubmit} className="flex flex-col items-center gap-3 w-full" style={{ maxWidth: 300 }}>
          <input
            ref={inputRef}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            autoComplete="current-password"
            autoCapitalize="off"
            autoCorrect="off"
            className="w-full px-4 py-3 rounded-lg font-mono text-sm outline-none transition-all duration-150"
            style={{
              backgroundColor: '#161b22',
              color: '#e6edf3',
              border: '1px solid #30363d',
              letterSpacing: '0.3em',
            }}
            onFocus={(e) => { e.target.style.borderColor = '#ff6b2b'; e.target.style.boxShadow = '0 0 0 2px rgba(255,107,43,0.2)'; }}
            onBlur={(e) => { e.target.style.borderColor = '#30363d'; e.target.style.boxShadow = 'none'; }}
            data-testid="login-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="font-mono text-sm tracking-wider transition-all duration-150"
            style={{
              color: '#ff6b2b',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.5 : 0.7,
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.opacity = '0.7'; }}
            data-testid="login-submit-button"
          >
            {loading ? 'VERIFYING...' : 'ENTER \u2192'}
          </button>
        </form>

        {error && (
          <p
            className="mt-4 font-mono text-sm"
            style={{ color: '#ef4444' }}
            data-testid="login-error-message"
          >
            {error}
          </p>
        )}
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 text-center px-6 pb-5 z-10"
        style={{ opacity: phase !== 'idle' ? 0 : 1, transition: 'opacity 200ms ease' }}
      >
        <p
          className="font-mono"
          style={{ color: '#8b949e', fontSize: 11 }}
          data-testid="login-disclaimer"
        >
          MacroFlare provides real-time news analysis for informational purposes only. Not financial advice. Not a recommendation to buy, sell, or hold any security or commodity.
        </p>
      </div>
    </div>
  );
}
